import type {
  StoryboardReviewProjection,
  StoryboardStructureProjection,
} from '@/lib/ai-session/contracts/types';

export type StoryboardWorkspacePhase =
  | 'preflight'
  | 'structure_generating'
  | 'structure_review'
  | 'detail_generating'
  | 'detail_review'
  | 'confirmed'
  | 'failed'
  | 'blocked';

export type StoryboardWorkspaceState = Readonly<{
  phase: StoryboardWorkspacePhase;
  canConfirmStructure: boolean;
  canSkipStructure: boolean;
  canConfirmStoryboard: boolean;
}>;

interface ResolveStoryboardWorkspaceStateInput {
  stageStatus?: string | null;
  review?: StoryboardReviewProjection;
  structure?: StoryboardStructureProjection;
  storyboardCount: number;
  hasUnassociatedScene: boolean;
}

const DETAIL_READY_C1_STATUSES = new Set(['not_required', 'confirmed', 'skipped']);
/** 详情稿可确认：后端主路径写 waiting_user；draft_ready 为工作区展示等价态。资产入库会把未写入草稿标成 dirty，草稿仍可确认。 */
const DETAIL_REVIEW_STAGE_STATUSES = new Set(['waiting_user', 'draft_ready', 'dirty']);

/** 正式写入后仍允许在当前分镜会话继续咨询或改稿；生成中或无草稿除外。 */
export function canFollowUpOnStoryboardBreakdown({
  hasDraft,
  stageStatus,
  isBusy,
}: {
  hasDraft: boolean;
  stageStatus: string | null | undefined;
  isBusy: boolean;
}): boolean {
  if (!hasDraft || isBusy) return false;
  return String(stageStatus ?? '') !== 'running';
}

export function resolveStoryboardWorkspaceState({
  stageStatus,
  review,
  structure,
  storyboardCount,
  hasUnassociatedScene,
}: ResolveStoryboardWorkspaceStateInput): StoryboardWorkspaceState {
  let phase: StoryboardWorkspacePhase;

  if (stageStatus === 'failed') {
    phase = 'failed';
  } else if (stageStatus === 'confirmed') {
    phase = 'confirmed';
  } else if (review?.c1Status === 'waiting_user' && structure?.status === 'waiting_user') {
    phase = 'structure_review';
  } else if (review?.c1Status === 'pending' || review?.c1Status === 'waiting_user') {
    phase = 'structure_generating';
  } else if (review && DETAIL_READY_C1_STATUSES.has(review.c1Status)) {
    phase =
      DETAIL_REVIEW_STAGE_STATUSES.has(String(stageStatus ?? '')) && storyboardCount > 0
        ? 'detail_review'
        : 'detail_generating';
  } else if (!review && ['created', 'running', 'dirty'].includes(String(stageStatus ?? ''))) {
    phase = 'preflight';
  } else {
    phase = 'blocked';
  }

  const isStructureReview = phase === 'structure_review';
  return {
    phase,
    canConfirmStructure: isStructureReview,
    canSkipStructure: isStructureReview,
    canConfirmStoryboard: phase === 'detail_review' && storyboardCount > 0 && !hasUnassociatedScene,
  };
}
